import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { LogOut, UserPen } from "lucide-react";

import { DASHBOARD_STACK, AUTH_STACK } from "../../utils/screenNames";
import NotificationsIcon from "../../assets/images/icons/notifications.svg";
import UserIcon from "../../assets/images/user.png";
import { logoutUser } from "../../redux/slices/authSlice";
import clubbieLogo from "../../assets/images/clubbie_logo_full.svg";
import {
  clearAllSelections,
  selectTournament,
  resetTournamentState,
} from "../../redux/slices/tournamentSlice";
import {
  fetchOrganisations,
  getSelectedOrganisation,
  selectOrganisation,
  updateSelectedOrganisation,
  resetTournamentState as resetOrgTournamentState,
} from "../../redux/slices/organisationSlice";
import EditProfileModal from "../../components/EditProfileModal/EditProfileModal";

import HomeTopBar from "./HomeTopBar";
import MemberTopBar from "./MemberTopBar";
import OrgMemberTopBar from "./OrgMemberTopBar";
import TopbarDropdowns from "./TopbarDropdowns";
import OrganisationTopBar from "./OrganisationTopBar";
import CompTopBar from "./CompTopBar";

const notificationFilter = "brightness(0) saturate(100%) invert(69%) sepia(11%) saturate(346%) hue-rotate(181deg) brightness(95%) contrast(90%)";

const TopBar = ({ editingOrganisation, onEditComplete }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const topbarDropdownRef = useRef(null);

  const {
    tournaments,
    selectedTournament,
    selectedCategory,
    loading: tournamentLoading,
    totalPages: tournamentTotalPages,
  } = useSelector((state) => state.tournament);


  const {
    organisations,
    selectedOrganisation,
  } = useSelector((state) => state.organisation);

  const user = useSelector((state) => state.user);
  const profileEmail = user?.user?.local?.email || user?.user?.google?.email || user?.user?.apple?.email || '';
  const profileName = user?.user?.profile?.fullName || '';
  const profileImage = user?.user?.profile?.image || '';
  const profileInitial = profileEmail ? profileEmail.charAt(0).toUpperCase() : '';

  const [showTopbarDropdown, setShowTopbarDropdown] = useState(false);
  const [isEditProfileModalOpen, setIsEditProfileModalOpen] = useState(false);

  // Fetch organisations on mount if not already loaded
  useEffect(() => {
    (async () => {
      try {
        // First fetch organisations to ensure we have the user's organisations
        await dispatch(fetchOrganisations({}));
        // Then get selected organisation (this will create one if none exists)
        await dispatch(getSelectedOrganisation());
      } catch (error) {
      }
    })();
  }, [dispatch]);

  const toggleTopbarDropdown = () => {
    setShowTopbarDropdown(!showTopbarDropdown);
  };

  const handleOrgChange = async (org) => {
    // Clear all tournament-related selections first
    dispatch(clearAllSelections());
    // Select the new organization
    dispatch(selectOrganisation(org));

    // Save organization selection to database
    try {
      await dispatch(updateSelectedOrganisation({ id: org.id })).unwrap();
    } catch (error) {
    }
  };


  const renderTopBar = () => {
    switch (location.pathname) {
      case DASHBOARD_STACK.HOME:
        return <HomeTopBar />;
        // return <CompTopBar />;
      case DASHBOARD_STACK.MEMBERS:
        return <MemberTopBar />;
      case DASHBOARD_STACK.FIXTURES:
        return null;
      case DASHBOARD_STACK.ORGANISATION_MEMBERS:
        return <OrgMemberTopBar />;
      case DASHBOARD_STACK.COMPETITIONS:
        return <CompTopBar />;
      case DASHBOARD_STACK.COMPETITIONS_CONFIG:
        return <CompTopBar />;
      case DASHBOARD_STACK.COMPETITIONS_VENUE:
        return <CompTopBar />;
      case DASHBOARD_STACK.COMPETITIONS_FEE:
        return <CompTopBar />;
      case DASHBOARD_STACK.ORGANISATIONS:
        return (
          <OrganisationTopBar
            editingOrganisation={editingOrganisation}
            onEditComplete={onEditComplete}
          />
        );
      default:
        return <HomeTopBar />;
    }
  };
  
  const competitionPaths = [
  DASHBOARD_STACK.COMPETITIONS,
  DASHBOARD_STACK.COMPETITIONS_CONFIG,
  DASHBOARD_STACK.COMPETITIONS_VENUE,
  DASHBOARD_STACK.COMPETITIONS_FEE
];

  const isCompetitionsPage = competitionPaths.includes(location.pathname);
  
  // const handleLogout = () => {
  //     dispatch(logoutUser())
  //       .unwrap()
  //       .then(() => {
  //         navigate(AUTH_STACK.LOGIN_OPTIONS, { replace: true });
  //       })
  //       .catch((error) => {
  //         // Still navigate even if API call fails
  //         navigate(AUTH_STACK.LOGIN_OPTIONS, { replace: true });
  //       });
  //   };

  // Close profile dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        showTopbarDropdown &&
        topbarDropdownRef.current &&
        !topbarDropdownRef.current.contains(e.target)
      ) {
        setShowTopbarDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showTopbarDropdown]);

  const handleEditProfile = () => {
    setShowTopbarDropdown(false);
    setIsEditProfileModalOpen(true);
  };

  const handleLogout = async () => {
    try {
      // Clear all Redux state first to prevent data leakage
      dispatch(clearAllSelections());
      dispatch(resetTournamentState());
      dispatch(resetOrgTournamentState());
      
      await dispatch(logoutUser()).unwrap(); // unwrap works with createAsyncThunk
    } catch (error) {
    } finally {
      navigate(AUTH_STACK.LOGIN_OPTIONS, { replace: true });
    }
  };


  return (
    <>
    {/* Topbar - Full Width */}
      <div className="w-full flex justify-between items-center px-6 py-4 border-b border-[#0D1B29] bg-[#000F1F] z-[100]">
        <div className="flex items-center">
          <img
            className="h-auto w-[174px]"
            src={clubbieLogo}
            alt="clubbie logo"
          />
        </div>

        <div className="flex items-center gap-4">
          <img
            className="w-6 h-6 cursor-pointer transition-[filter] duration-200 hover:brightness-0 hover:invert"
            style={{ filter: notificationFilter }}
            src={NotificationsIcon}
            alt="notifications"
          />

          <div className="relative" ref={topbarDropdownRef}>
            <button
              className="w-9 h-9 rounded-full overflow-hidden border-2 border-app-border cursor-pointer bg-app-surface flex items-center justify-center"
              onClick={toggleTopbarDropdown}
            >
              {profileImage ? (
                <img
                  src={profileImage}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center font-semibold text-sm text-[#FEFCEB]">
                  {profileInitial}
                </div>
              )}
            </button>

            {showTopbarDropdown && (
              <div className="absolute top-full right-0 mt-2 w-64 bg-[#0A1929] border border-app-border rounded-2xl shadow-xl overflow-hidden z-[1000]">
                {/* User info */}
                <div className="px-4 py-3 border-b border-app-border">
                  <p className="text-sm font-medium text-app-text truncate">
                    {profileName}
                  </p>
                  <p className="text-xs text-app-text-secondary truncate">
                    {profileEmail}
                  </p>
                </div>

                {/* Actions */}
                <div className="py-1">
                  <button
                    className="w-full text-left px-4 py-3 text-sm text-app-text hover:bg-app-hover transition-colors flex items-center gap-3 cursor-pointer"
                    onClick={handleEditProfile}
                  >
                    <UserPen size={16} className="text-app-text-secondary" />
                    Edit profile
                  </button>
                </div>

                {/* Divider + Logout */}
                <div className="border-t border-app-border py-1">
                  <button
                    className="w-full text-left px-4 py-3 text-sm text-red-400 hover:bg-red-500/10 transition-colors flex items-center gap-3 cursor-pointer"
                    onClick={handleLogout}
                  >
                    <LogOut size={16} />
                    Log out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

    <EditProfileModal
      isOpen={isEditProfileModalOpen}
      onClose={() => setIsEditProfileModalOpen(false)}
    />
    </>
  );
};

export default React.memo(TopBar);
